import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Spinner from '../components/common/Spinner';
import { generateReportHTML } from '../services/reportPreviewService';
import { requestPDF } from '../services/pdfService';
import { patientService } from '../services/patientService';
import { reportService } from '../services/reportService';
import { generatePDFFilename } from '../utils/generatePDFFilename';

const ReportPreviewPage = () => {
    const { patientId } = useParams();
    const location = useLocation();
    const [htmlContent, setHtmlContent] = useState('');
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [patientName, setPatientName] = useState(location.state?.patientName || '');
    const [orderId, setOrderId] = useState(location.state?.orderId || '');

    useEffect(() => {
        const createPreview = async () => {
            try {
                // Use reportData from location.state if available
                let reportData = location.state?.reportData;

                // If no reportData in state, fetch it
                if (!reportData) {
                    const [{ data: patientResp }, { data: reportResp }] = await Promise.all([
                        patientService.getPatientById(patientId),
                        reportService.getReportByPatientId(patientId),
                    ]);

                    const patient = patientResp?.data;
                    const report = reportResp?.data;

                    if (!patient || !report) {
                        throw new Error('Unable to fetch required data for preview');
                    }

                    reportData = {
                        patientName: patient?.profile?.name,
                        height: patient?.profile?.height,
                        weight: patient?.profile?.weight,
                        age: patient?.profile?.age,
                        gender: patient?.profile?.gender,
                        dob: patient?.profile?.dob,
                        ...report,
                    };
                }

                // Always fetch patient info for filename (separate from reportData)
                const { data: patientResp } = await patientService.getPatientById(patientId);
                const patient = patientResp?.data;

                const fetchedPatientName = patient?.profile?.name;
                const fetchedOrderId = patient?.sukra_order_id || patient?.id;

                // Update state with patient info for download
                setPatientName(fetchedPatientName || '');
                setOrderId(fetchedOrderId || '');

                console.log('📄 Preview Page - Patient Info:');
                console.log('Patient Name:', fetchedPatientName);
                console.log('Order ID:', fetchedOrderId);

                const html = await generateReportHTML(reportData);
                setHtmlContent(html);
            } catch (error) {
                console.error('Failed to generate preview', error);
                setHtmlContent('<p class="text-red-600">Failed to generate preview</p>');
            } finally {
                setLoading(false);
            }
        };

        createPreview();
    }, [patientId, location.state]);

    const handleDownload = async () => {
        if (!htmlContent) return;
        try {
            setDownloading(true);
            const blob = await requestPDF(htmlContent);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const finalPatientName = patientName || 'patient';
            const finalOrderId = orderId || patientId;
            const fileName = generatePDFFilename(finalPatientName, 'dexa', finalOrderId);
            
            console.log('🔍 Download Debug Info:');
            console.log('patientName state:', patientName);
            console.log('orderId state:', orderId);
            console.log('Final filename:', fileName);
            
            a.download = fileName;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('PDF generation failed', err);
        } finally {
            setDownloading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-full py-12">
                <Spinner size="lg" />
            </div>
        );
    }

    return (
        <Layout showSidebar={false}>
            <div className="flex justify-end mb-4 gap-4">
                <button
                    onClick={handleDownload}
                    disabled={downloading || !htmlContent}
                    className="px-4 py-2 bg-primary-500 text-white rounded disabled:opacity-50"
                >
                    {downloading ? 'Generating…' : 'Download PDF'}
                </button>
                <button
                    onClick={() => {
                        if (htmlContent) {
                            const win = window.open('', '_blank');
                            win.document.write(htmlContent);
                            win.document.close();
                        }
                    }}
                    disabled={!htmlContent}
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50"
                >
                    View in New Tab
                </button>
            </div>
            <iframe
                title="Report Preview"
                srcDoc={htmlContent}
                className="w-full min-h-screen border-none"
                sandbox="allow-same-origin allow-scripts allow-popups"
            />
        </Layout>
    );
};

export default ReportPreviewPage; 
